import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';

const { FiShield, FiAlertTriangle, FiCheckCircle, FiClock, FiPhone, FiMessageCircle, FiAlertCircle } = FiIcons;

const VirusArticle = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO Meta Tags - Optimized for local areas
    const seoData = {
      es: {
        title: 'Eliminación de Virus Cartagena, La Manga y San Javier | Servicio Rápido 24/7',
        description: 'Servicio experto de eliminación de virus y malware en Cartagena, La Manga del Mar Menor y San Javier. Limpieza completa, protección antivirus. Disponible 24/7. ☎️ 642 643 499'
      },
      en: {
        title: 'Virus Removal Cartagena, La Manga & San Javier | Fast 24/7 Service',
        description: 'Expert virus and malware removal service in Cartagena, La Manga del Mar Menor and San Javier. Complete cleaning, antivirus protection. Available 24/7. ☎️ 642 643 499'
      },
      it: {
        title: 'Rimozione Virus Cartagena, La Manga e San Javier | Servizio Rapido 24/7',
        description: 'Servizio esperto di rimozione virus e malware a Cartagena, La Manga del Mar Menor e San Javier. Pulizia completa, protezione antivirus. Disponibile 24/7. ☎️ 642 643 499'
      }
    };

    document.title = seoData[language].title;
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = seoData[language].description;

    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = seoData[language].title;

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.content = seoData[language].description;
  }, [language]);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <section className="bg-gradient-to-br from-red-600 to-red-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-full mb-6">
              <SafeIcon icon={FiShield} className="w-5 h-5" />
              <span className="font-semibold">{t('articles.virus.category')}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t('articles.virus.title')}
            </h1>
            <p className="text-xl text-red-100 mb-8">
              {t('articles.virus.subtitle')}
            </p>
            <div className="flex items-center justify-center space-x-6 text-sm">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiClock} className="w-4 h-4" />
                <span>{t('articles.readTime')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
                <span>{t('articles.updated')}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8"
      >
        <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-red-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiAlertCircle} className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">{t('articles.cta.virus')}</h3>
                <p className="text-sm text-gray-600">{t('articles.cta.immediate')}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open('https://wa.me/34642643499', '_blank')}
                className="flex items-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold"
              >
                <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
                <span>WhatsApp</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.location.href = 'tel:+34642643499'}
                className="flex items-center space-x-2 bg-red-600 text-white px-6 py-3 rounded-lg font-semibold"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span>{t('articles.cta.call')}</span>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          <img
            src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
            alt={t('articles.virus.title')}
            className="w-full h-96 object-cover rounded-2xl mb-8 shadow-lg"
          />

          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {t('articles.virus.intro.title')}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('articles.virus.intro.p1')}
            </p>
            <p className="text-gray-700 leading-relaxed">
              {t('articles.virus.intro.p2')}
            </p>
          </div>

          <div className="bg-red-50 border-l-4 border-red-600 rounded-lg p-6 mb-8">
            <h3 className="font-bold text-red-900 mb-4 flex items-center">
              <SafeIcon icon={FiAlertTriangle} className="w-6 h-6 mr-2" />
              {t('articles.virus.signs.title')}
            </h3>
            <ul className="space-y-2">
              {['sign1', 'sign2', 'sign3', 'sign4', 'sign5', 'sign6'].map((key) => (
                <li key={key} className="flex items-start space-x-2">
                  <span className="text-red-600 font-bold">•</span>
                  <span className="text-gray-700">{t(`articles.virus.signs.${key}`)}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {t('articles.virus.types.title')}
            </h2>
            <div className="space-y-6">
              {['type1', 'type2', 'type3', 'type4', 'type5'].map((key) => (
                <div key={key} className="border-l-4 border-blue-600 pl-4">
                  <h3 className="font-bold text-gray-900 mb-2">
                    {t(`articles.virus.types.${key}.title`)}
                  </h3>
                  <p className="text-gray-700">
                    {t(`articles.virus.types.${key}.description`)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {t('articles.virus.service.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {['step1', 'step2', 'step3', 'step4'].map((key, index) => (
                <div key={key} className="bg-white rounded-xl p-6 shadow">
                  <div className="w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center font-bold text-xl mb-4">
                    {index + 1}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">
                    {t(`articles.virus.service.${key}.title`)}
                  </h3>
                  <p className="text-gray-700">
                    {t(`articles.virus.service.${key}.description`)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {t('articles.virus.prevention.title')}
            </h2>
            <div className="space-y-4">
              {['tip1', 'tip2', 'tip3', 'tip4', 'tip5'].map((key) => (
                <div key={key} className="flex items-start space-x-3 bg-green-50 p-4 rounded-lg">
                  <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">
                    {t(`articles.virus.prevention.${key}`)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-600 to-red-800 text-white rounded-2xl p-8 text-center">
            <h2 className="text-3xl font-bold mb-4">
              {t('articles.virus.finalCta.title')}
            </h2>
            <p className="text-xl mb-6 text-red-100">
              {t('articles.virus.finalCta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open('https://wa.me/34642643499?text=Urgente%3A%20necesito%20eliminaci%C3%B3n%20de%20virus', '_blank')}
                className="flex items-center justify-center space-x-2 bg-green-600 text-white px-8 py-4 rounded-xl font-semibold"
              >
                <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
                <span>{t('articles.virus.finalCta.whatsapp')}</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/#contact')}
                className="flex items-center justify-center space-x-2 bg-white text-red-600 px-8 py-4 rounded-xl font-semibold"
              >
                <SafeIcon icon={FiShield} className="w-5 h-5" />
                <span>{t('articles.virus.finalCta.contact')}</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </article>
    </div>
  );
};

export default VirusArticle;